/**
 * Core Types for Luca Application
 * 
 * This file contains all shared type definitions used across the application.
 */

export enum Role {
    USER = 'user',
    SYSTEM = 'system',
    MODEL = 'model',
    ASSISTANT = 'assistant'
}

// ==================== ACCOUNT TYPES ====================

export type AccountTier = 'standard' | 'premium' | 'max';

export interface RankTierConfig {
    id: AccountTier;
    name: string;
    displayName?: string;
    title?: string;
    description: string;

    // Pricing
    price: number;
    monthlyPrice?: number;
    yearlyPrice?: number;

    // Token Limits
    tokenLimit: number;
    tokenRefreshRate: number;
    refreshIntervalMinutes: number;

    // Feature Limits
    dailyAttachmentLimit: number;
    maxFileSize: number; // MB
    maxFileSizeMB?: number; // Alias
    maxSessionHistory: number;
    exportLimit?: number;
    quizLimit?: number;
    essayGradingLimit?: number;

    // AI Configuration
    aiModel: string;
    defaultModel?: string; // Alias
    advancedModelsAccess: boolean;
    priorityQueue: boolean;
    features: string[];

    // Styling (UI V2)
    color: string;
    gradientFrom: string;
    gradientTo: string;
    iconEmoji: string;
    badgeText?: string;

    // Legacy Styling (Optional)
    colors?: {
        primary: string;
        secondary: string;
        gradientFrom: string;
        gradientTo: string;
        text: string;
        background: string;
        chatBubble: string;
        chatBubbleText: string;
        avatarBorder: string;
        glowColor: string;
    };
    badge?: {
        text: string;
        icon: string;
        showInHeader: boolean;
        showInSidebar: boolean;
    };

    // Metadata
    displayOrder: number;
    isActive: boolean;
    showInUpgradeModal: boolean;
}

// ==================== USER TYPES ====================

export interface QuizAttempt {
    id: string;
    topic: string;
    score: number;
    totalQuestions: number;
    percentage: number;
    date: string | number;
    difficulty?: 'easy' | 'medium' | 'hard';
    timeSpent?: number; // seconds
}

export interface MockExamAttempt {
    id: string;
    subject: string;
    score: number;
    totalQuestions: number;
    percentage: number;
    date: string | number;
    timeTaken: number; // minutes
    sections?: { name: string; score: number; total: number; }[];
}

export interface User {
    id: string;
    email: string;
    name: string;
    displayName?: string;
    photoURL?: string;

    // Account Type
    accountType: AccountTier;
    role?: 'user' | 'admin' | 'moderator';
    isAnonymous?: boolean;

    // Token System
    tokens: number;
    maxTokens: number;
    tokensLastRefreshed?: string;
    lastTokenIncrementTime?: string;

    // Gamification
    totalXP: number;
    level: number;
    streak: number;

    // Subscription
    subscriptionEndDate?: string;
    subscriptionStartDate?: string;
    subscriptionMonths?: number;

    // Usage Stats
    totalQuestions?: number;
    totalQuizzes?: number;
    totalExams?: number;

    // Timestamps
    createdAt: string;
    lastActive?: string | number;
    lastLoginAt?: string;

    // Settings
    preferredModel?: string;
    chatBackground?: string;
    darkMode?: boolean;

    // Additional Properties
    referralCode?: string;
    referralCount?: number; // New: Track successful referrals
    referredBy?: string;
    referralHistory?: Array<{
        userId: string;
        username: string;
        timestamp: string;
        tokensAwarded: number;
        milestone: number | null;
    }>;
    source?: string;
    branch?: string;

    // Partnership
    partnershipStatus?: 'none' | 'pending' | 'approved' | 'rejected'; // New: JPIA Partnership status

    // Gamification State
    gamificationState?: any;
    rank?: string;
    xp?: number;
    currentStreak?: number;
    bestStreak?: number;
    quizzesPassed?: number;
    achievementsUnlocked?: number;

    // Activity Stats
    quizHistory?: QuizAttempt[];
    mockExamHistory?: MockExamAttempt[];
    quizAttempts?: number;
    essayGradings?: number;
    totalStudyMinutes?: number;

    // Missing Properties
    username?: string;
    notifications?: any[];
    isMax?: boolean;
    isPremium?: boolean;
    accountTier?: AccountTier; // Note: accountType is already defined above, but accountTier is used in some places
    tokenBalance?: number;
    unlockedBackgrounds?: string[]; // IDs of unlocked chat backgrounds
    lastTokenRefresh?: number;
    dailyTokenUsage?: number;
    dailyAttachmentCount?: number;
    lastResetTime?: string | number;
    examMode?: string;
    tier?: string; // Alternative to accountType, used in some admin panels
    isBanned?: boolean;
    bannedAt?: number;
    bannedReason?: string;
    premiumExpiryDate?: string;
    premiumStartDate?: string;
    lastTokenPurchase?: string;
    totalTokensPurchased?: number;
    purchaseCount?: number;
    activePromoEffects?: {
        unlimitedTokensUntil?: string;
        tokenMultiplier?: number;
        tokenMultiplierUntil?: string;
        unlockedFeatures?: string[];
        featureUnlocksUntil?: string;
    };
    isMaxTier?: boolean;
    adminNotes?: string;
    guestMessageCount?: number;
    requiresPasswordChange?: boolean;
    dailyMessageCount?: number;
    // lastActive is already defined above as string | undefined. If number is needed, update the original definition.

    // Auth related
    googleId?: string;
    picture?: string;
    firstName?: string;
    lastName?: string;
    password?: string; // Only used for legacy/local auth, not stored in client usually but used in AuthModal
    secretSentence?: string; // Secret phrase for password recovery
    authProvider?: 'email' | 'google' | 'facebook' | 'apple';
    lastMessageDate?: string;

    // Device Tracking
    devices?: UserDevice[];

    // Duplicate Account Detection
    deviceFingerprint?: string;
    knownIPs?: string[];
    flaggedAsDuplicate?: boolean;
    duplicateOf?: string; // ID of the primary account
    duplicateConfidence?: number; // 0-100
    accountRestricted?: boolean;
    restrictedReason?: string;
    restrictedAt?: number;

    // Special Links
    specialLinkUsed?: string;
    specialLinkUsedAt?: number;

    // Admin flag
    isAdmin?: boolean;

    // Gamification extras
    questsCompleted?: number;
    achievements?: any[];
    streakCount?: number;

    // Bonus tokens tracking
    bonusTokens?: number;
}

export interface UserDevice {
    id: string;
    name: string;
    type: 'desktop' | 'mobile' | 'tablet';
    os: string;
    browser: string;
    lastActive: string;
    firstActive: string;
    isActive?: boolean; // UI only property
    ip?: string;
    location?: string;
    totalUsageMinutes?: number;
}

export interface DuplicateAccountFlag {
    id: string;
    primaryAccountId: string;
    duplicateAccountId: string;
    confidence: number; // 0-100
    reasons: string[];
    detectedAt: string;
    resolvedAt?: string;
    resolvedBy?: string; // admin ID
    resolution?: 'merged' | 'dismissed' | 'restricted';
    matchedFingerprint?: boolean;
    matchedIP?: boolean;
    matchedDevice?: boolean;
    matchedEmail?: boolean;
}

// ==================== MESSAGE TYPES ====================

// Role is now defined as an Enum at the top of the file

export type MessageDensity = 'compact' | 'comfortable' | 'spacious';

export interface Message {
    id: string;
    role: Role;
    content: string;
    timestamp: string | Date;

    // Attachments
    attachments?: Attachment[];

    // AI Response Metadata
    model?: string;
    tokensUsed?: number;
    processingTime?: number;

    // Quiz/Content
    quiz?: any;
    chart?: any;
    tAccount?: any;

    // Feedback
    feedback?: 'positive' | 'negative' | string;
    feedbackComment?: string;

    // Status
    isStreaming?: boolean;
    error?: string;
    isError?: boolean;
    thoughtProcess?: string;
    thought?: string;
    tokensCharged?: number;
    tokenCost?: number;
    modelUsed?: string;

    // AI Mode
    persona?: string;
    depth?: string;
    referencedAttachments?: Attachment[] | string[];
    budgetAdjusted?: number;

    // Whiteboard Content
    hasWhiteboardContent?: boolean;
    whiteboardContent?: string;
}

// ==================== ATTACHMENT TYPES ====================

export interface Attachment {
    id?: string;
    name: string;
    type?: string;
    size?: number;
    url?: string;
    base64?: string;
    mimeType?: string;
    thumbnailUrl?: string;
    extractedText?: string;
    pageCount?: number;
    data?: string;
    preview?: string;
    annotations?: PdfAnnotation[];
}

export interface PdfAnnotation {
    id: string;
    page: number;
    x: number;
    y: number;
    text?: string;
    color?: string;
    timestamp?: string;
    createdAt?: string;
}

// ==================== SESSION TYPES ====================

export interface ChatSession {
    id: string;
    title: string;
    userId?: string;

    // Messages
    messages: Message[];

    // Context
    topic?: string;
    mode?: string;
    persona?: string;

    // Timestamps
    createdAt: string | number;
    updatedAt: string | number;
    lastMessageAt?: string | number;
    lastModified?: string | number | Date;

    // Settings
    pinned?: boolean;
    isPinned?: boolean;
    archived?: boolean;

    // Study Mode
    studyMode?: ExamMode;
    studyDocument?: any;
    pinnedMessages?: Message[] | string[];
    generatedMaterials?: GeneratedMaterial[];
    tags?: string[];
    lastSyncedAt?: string;
    syncStatus?: 'synced' | 'pending' | 'conflict' | 'offline' | string;
    version?: number;
}

// ==================== EXAM/QUIZ TYPES ====================

export type ExamMode =
    | 'far'
    | 'afar'
    | 'aud'
    | 'mas'
    | 'tax'
    | 'rfbt'
    | 'general'
    | 'legal'
    | 'custom'
    | string;

export interface QuizQuestion {
    id: string;
    text: string;
    topic?: string;
    options: {
        id: string;
        text: string;
        isCorrect: boolean;
        explanation?: string;
    }[];
    difficulty?: 'easy' | 'medium' | 'hard';
    explanation?: string;
    type?: 'multiple_choice' | 'essay';
}

export interface Quiz {
    id: string;
    title: string;
    questions: QuizQuestion[];
    topic?: ExamMode;
    createdAt: string;
    score?: number;
    completed?: boolean;
}

// ==================== GENERATED MATERIALS ====================

export interface GeneratedMaterial {
    id: string;
    type: 'flashcards' | 'summary' | 'outline' | 'quiz' | 'notes' | 'exam' | string;
    title: string;
    content: any;
    data?: any;
    topic?: ExamMode;
    createdAt: string;
    userId: string;
    sessionId?: string;
    tags?: string[];
    itemCount?: number;
}

// ==================== PROMO CODE TYPES ====================

export interface PromoCode {
    id?: string;
    code: string;
    type: 'discount' | 'tokens' | 'subscription' | 'trial' | 'background' | 'percent' | 'fixed';

    // Effect type (used by PromoCodesPanel)
    effect?: 'discount' | 'token_boost' | 'premium_trial' | 'unlimited_tokens' | 'feature_unlock' | 'background_unlock' | 'custom_effect';

    // Discount
    discountPercent?: number;
    discountAmount?: number;
    value?: number; // Generic value for discount amount or percentage

    // Tokens
    tokenAmount?: number;
    tokenBoostAmount?: number;
    tokenBoostMultiplier?: number;

    // Subscription / Premium
    subscriptionDays?: number;
    subscriptionTier?: AccountTier;
    premiumDurationDays?: number;

    // Background
    chatBackgroundId?: string;

    // Feature Unlock
    featureUnlocks?: string[];
    customEffectId?: string;

    // Usage Limits
    maxUses?: number;
    usedCount: number;
    usedBy?: string[];
    usedByUsers?: string[];
    oneTimePerUser?: boolean;
    minPurchaseAmount?: number;

    // Usage History
    usageHistory?: PromoCodeUsageRecord[];

    // Validity
    validFrom?: string;
    validUntil?: string;
    expiryDate?: string;
    isActive: boolean;

    // Metadata
    createdAt: string;
    createdBy?: string;
    description?: string;
    category?: 'general' | 'holiday' | 'partnership' | 'affiliate' | 'loyalty' | 'special';
}

export interface PromoCodeUsageRecord {
    userId: string;
    username?: string;
    usedAt: string;
    effect?: string;
    valueApplied?: number;
}

export interface PromoCodeUsage {
    id?: string;
    promoCodeId?: string;
    userId: string;
    username?: string;
    usedAt: string;
    effect?: string;
    valueApplied?: number;
    benefit?: {
        type: string;
        value: any;
    };
}

// ==================== SPECIAL LINKS ====================

export interface SpecialRegistrationLink {
    id: string;
    code: string;
    name: string;
    description?: string;

    // Benefits
    accountType?: AccountTier;
    bonusTokens?: number;
    subscriptionDays?: number;

    // Limits
    maxUses?: number;
    usedCount: number;

    // Validity
    validUntil?: string;
    expiresAt?: string;
    isActive: boolean;

    // Usage tracking
    usedBy?: string[];
    lastUsedAt?: number;

    // Metadata
    createdAt: string | number;
    createdBy?: string;
    rewards?: {
        tokens?: number;
        premiumDays?: number;
        accountTier?: AccountTier;
        unlimitedTokensDays?: number;
        tokenMultiplier?: number;
        tokenMultiplierDays?: number;
    };
}

// ==================== GLOBAL CONFIG ====================

export interface GlobalConfig {
    // Token Limits
    freeDailyLimit: number;
    freeTokenLimit: number;
    premiumTokenLimit: number;
    maxTokenLimit: number;

    // Attachment Limits
    freeAttachmentLimit: number;
    premiumFileLimit: number;

    // AI Models
    freeModel: string;
    premiumModel: string;
    maxModel: string;

    // Pricing
    proPrice: number;
    maxPrice: number;

    // Sales
    isSaleActive: boolean;
    saleDiscountPercent: number;

    // Token Refresh
    limitResetIntervalHours: number;
    standardRefreshRate: number;
    premiumRefreshRate: number;
    maxRefreshRate: number;
    refreshIntervalMinutes: number;

    // API
    apiKey: string;

    // Welcome
    welcomeTitle: string;
    welcomeMessage: string;
    welcomeFeatures: string[];

    // Extension Discounts
    extensionDiscounts: {
        months3: number;
        months6: number;
        months12: number;
    };

    // Token Warning
    tokenWarningThreshold: number;

    // Auto Budget
    minBudgetPerRequest: number;
    maxBudgetPerRequest: number;

    // PayPal
    paypal: {
        clientId: string;
        environment: 'sandbox' | 'live';
        clientSecret?: string;
        webhookId?: string;
    };

    // Additional Config (optional)
    maintenanceMode?: boolean;
    maintenanceMessage?: string;
    guestMessageLimit?: number;
    guestDailyLimit?: number;
    apkConfig?: {
        isEnabled: boolean;
        downloadUrl?: string;
        version?: string;
        googleDriveFolderId?: string;
        googleDriveApiKey?: string;
        autoFetchLatest?: boolean;
    };
    rankTiers?: RankTierConfig[];
    maintenanceETA?: string;
    exportCosts?: {
        singleResponseDocx: number;
        singleResponsePdf: number;
        compiledDocx: number;
        compiledPdf: number;
        perAdditionalResponse: number;
    };

    // Referral System Config
    referralRewards?: {
        enabled: boolean;
        rewardPerReferralAfterCap: number; // hours of premium
        milestones: {
            requiredReferrals: number;
            rewardType: 'credits' | 'background' | 'premium' | 'mixed';
            rewardValue?: number; // credits or days
            rewardDescription: string;
            icon: string; // lucide icon name
        }[];
    };

    // New Detailed Configs
    referralConfig?: ReferralConfig;
    jpiaConfig?: JPIAConfig;

    // Feature Access Control
    featureAccess?: FeatureAccessConfig;

    // Token Packages (can be object or array depending on admin panel usage)
    tokenPackages?: any;

    // Essay Token Costs
    essayTokenCosts?: {
        basic?: number;
        detailed?: number;
        [key: string]: number | undefined;
    };

    // Site Content
    siteContent?: Record<string, any>;

    // IP Rate Limiting
    ipRateLimitConfig?: {
        enabled?: boolean;
        maxRequestsPerMinute?: number;
        [key: string]: any;
    };

    // Upgrade Button Config
    upgradeButtonConfig?: Record<string, any>;

    // Token Modal Config
    tokenModalConfig?: Record<string, any>;
}

// ==================== FEATURE ACCESS CONFIG ====================

export type FeatureMinimumTier = 'standard' | 'premium' | 'max' | 'disabled';

export interface FeatureGateConfig {
    minimumTier: FeatureMinimumTier;
    enabled: boolean;
    label?: string;
    description?: string;
    // Tier-based granular limits (optional)
    tierLimits?: {
        // Readables: max pages per tier
        standardMaxPages?: number;
        premiumMaxPages?: number;
        maxMaxPages?: number;
        // Readables: depth restrictions (minimum tier required for each depth)
        detailedMinTier?: FeatureMinimumTier;
        comprehensiveMinTier?: FeatureMinimumTier;
        // QuickBites: complexity restrictions
        expertMinTier?: FeatureMinimumTier;
        advancedMinTier?: FeatureMinimumTier;
    };
}

export interface FeatureAccessConfig {
    quickBites: FeatureGateConfig;
    readables: FeatureGateConfig;
    mockExam: FeatureGateConfig;
    practiceProblems: FeatureGateConfig;
    studyGuide: FeatureGateConfig;
    quiz: FeatureGateConfig;
    groupStudy: FeatureGateConfig;
    directMessaging: FeatureGateConfig;
    leaderboard: FeatureGateConfig;
    activityFeed: FeatureGateConfig;
    [key: string]: FeatureGateConfig;
}

// ==================== ACCOUNTING TYPES ====================

export enum AccountingTopic {
    GENERAL = 'general',
    FAR = 'far',
    AFAR = 'afar',
    AUD = 'aud',
    MAS = 'mas',
    TAX = 'tax',
    RFBT = 'rfbt',
}

export interface StandardOption {
    id: string;
    label: string;
    description: string;
}

// ==================== NOTIFICATION TYPES ====================

export interface Notification {
    id: string;
    type: 'info' | 'success' | 'warning' | 'error' | 'achievement' | 'streak' | 'levelup' | 'gift' | 'announcement';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
    action?: {
        label: string;
        url?: string;
        callback?: string;
    };
}

// Extended notification type for user announcements
export interface UserNotification {
    id: string;
    type: 'announcement' | 'info' | 'success' | 'warning' | 'error' | 'achievement' | 'streak' | 'levelup' | 'gift';
    title: string;
    message: string;
    timestamp: number;
    read: boolean;
    announcementId?: string;
    fullContent?: string;
    action?: {
        label: string;
        url?: string;
        callback?: string;
    };
}

// ==================== FRIEND/SOCIAL TYPES ====================

export interface Friend {
    id: string;
    name: string;
    email?: string;
    photoURL?: string;
    accountType: AccountTier;
    level: number;
    totalXP: number;
    streak: number;
    status: 'pending' | 'accepted' | 'blocked';
    addedAt: string;
    lastActive?: string;
}

// ==================== LEADERBOARD TYPES ====================

export interface LeaderboardEntry {
    userId: string;
    name: string;
    photoURL?: string;
    accountType: AccountTier;
    level: number;
    totalXP: number;
    streak: number;
    rank: number;
    badge?: string;
}

// ==================== STUDY ANALYTICS ====================

export interface StudySession {
    id: string;
    userId: string;
    startTime: string;
    endTime?: string;
    duration: number; // in minutes
    topic?: ExamMode;
    questionsAsked: number;
    quizzesCompleted: number;
    xpEarned: number;
}

export interface StudyStats {
    totalStudyTime: number;
    totalQuestions: number;
    totalQuizzes: number;
    totalXP: number;
    currentStreak: number;
    longestStreak: number;
    averageSessionDuration: number;
    topTopics: { topic: string; count: number }[];
    weeklyProgress: { day: string; minutes: number }[];
}

// ==================== EXPORT TYPES ====================

export interface ExportOptions {
    format: 'pdf' | 'docx' | 'txt' | 'md';
    includeMessages: boolean;
    includeAttachments: boolean;
    includeMetadata: boolean;
    dateRange?: {
        from: string;
        to: string;
    };
}

// ==================== ERROR TYPES ====================

export interface AppError {
    code: string;
    message: string;
    details?: any;
    timestamp: string;
    userId?: string;
    sessionId?: string;
}

// ==================== API TYPES ====================

export interface APIResponse<T = any> {
    success: boolean;
    data?: T;
    error?: {
        code: string;
        message: string;
    };
    meta?: {
        timestamp: string;
        requestId: string;
    };
}

// ==================== PAYMENT TYPES ====================

export interface PaymentTransaction {
    id: string;
    userId: string;
    type?: 'subscription' | 'tokens' | 'upgrade';
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed' | 'refunded';
    provider?: 'paypal' | 'paymongo' | 'gcash' | 'manual';
    providerId?: string;
    createdAt: string | number;
    completedAt?: string | number;
    metadata?: any;
    username?: string;
    months?: number;
    notes?: string;
    paypalEmail?: string;
}

// ==================== FEATURE FLAGS ====================
export type AdPosition = 'popup' | 'header' | 'toast' | 'sidebar';

export type AdFrequency = 'once' | 'session' | 'daily' | 'every-visit' | 'every-interaction' | 'interval';

export interface AdBanner {
    id: string;
    position: AdPosition;
    isActive: boolean;
    startDate: string | Date;
    endDate: string | Date;
    probability: number;
    delaySeconds: number;
    design: string;
    title: string;
    content: string;
    actionUrl?: string;
    actionLabel?: string;
    impressions?: number;
    clicks?: number;
    // Frequency controls
    frequency?: AdFrequency;
    intervalMinutes?: number; // For 'interval' frequency
    maxImpressionsPerUser?: number; // Cap total impressions per user
    // Custom styling
    customColors?: {
        background?: string;
        text?: string;
        button?: string;
        buttonText?: string;
    };
    fontSize?: 'small' | 'medium' | 'large';
    fontWeight?: 'normal' | 'semibold' | 'bold';
    textAlign?: 'left' | 'center' | 'right';
    buttonStyle?: 'solid' | 'outline' | 'ghost';
    // Visual enhancements
    imageUrl?: string;
    imagePosition?: 'background' | 'left' | 'right' | 'top' | 'bottom';
    pattern?: 'none' | 'dots' | 'grid' | 'waves' | 'diagonal' | 'circles';
    patternOpacity?: number;

    // Advanced Styling
    styleConfig?: {
        backgroundColor?: string;
        textColor?: string; // Fallback for general text
        titleColor?: string; // Specific for title
        contentColor?: string; // Specific for body content
        borderColor?: string;
        borderWidth?: number;
        borderRadius?: number;
        shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'inner';
        width?: string;
        height?: string;
        padding?: string;
    };
    customCss?: string; // For unlimited customizability

    // Targeting
    targetAudience?: 'all' | 'free' | 'premium' | 'max' | 'new_users' | 'inactive_users';
    targetPages?: string[]; // e.g. ['/dashboard', '/exam']
    priority?: number; // Higher priority ads show first
}

// ==================== PARTNERSHIP TYPES ====================

export interface PartnerSchool {
    id: string;
    name: string;
    code: string; // e.g., 'UST-JPIA'
    logoUrl?: string;
    isActive: boolean;
    memberCount?: number;
    contactPerson?: string;
    email?: string;
    benefits?: string[]; // Specific benefits for this school
    createdAt: string;
}

export interface JPIAConfig {
    isEnabled: boolean;
    bannerTitle: string;
    bannerDescription: string;
    bannerImageUrl?: string;
    globalBenefits: string[]; // Benefits applicable to all partners
    partners: PartnerSchool[];
    applicationEmail?: string; // Where notifications go
}

export interface ReferralTier {
    id: string;
    minReferrals: number;
    name: string; // e.g., "Silver", "Gold"
    rewardDescription: string;
    icon: string;
    color: string;
}

export interface ReferralConfig {
    enabled: boolean;
    baseRewardTokens: number;
    baseRewardPremiumDays: number;
    tiers: ReferralTier[];
    maxReferralsPerUser?: number; // Cap
    allowSelfReferral: boolean; // Usually false
}
export interface FeatureFlags {
    enableChat: boolean;
    enableQuiz: boolean;
    enableFlashcards: boolean;
    enableTools: boolean;
    enableCommunity: boolean;
    enableShop: boolean;
    enableLegalEthics: boolean;
    enableTaxation: boolean;
    enableGamification: boolean;
    enableSocialFeatures: boolean;
}

// ==================== THEME TYPES ====================

export interface ThemeConfig {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    fontFamily: string;
    borderRadius: 'sharp' | 'rounded' | 'pill';
    darkMode: boolean;
}

// ==================== TRANSACTION TYPE ====================

export type Transaction = PaymentTransaction;

// ==================== ONBOARDING TYPES ====================

export interface OnboardingSlide {
    id: string;
    title: string;
    description: string;
    icon?: any;
    image?: string;
    visual?: string;
    features?: string[];
    examples?: string[];
    tools?: { name: string; text?: string; icon?: any; description?: string }[];
    order?: number;
    isActive?: boolean;
}
